/**
 * The table before the cards, shared by the lobby and the invite page: the
 * players round the edge and the deck in the middle, where it will be dealt
 * from.
 *
 * Laid out as a grid (north across the top, west, deck and east across the
 * middle, south across the bottom) rather than placed absolutely against the
 * ring the way the play screen is. A lobby seat carries buttons, and absolutely
 * placed boxes on a short phone ran into one another: the code's caption was
 * drawn over the south seat's name on a 320 × 568 screen. A grid cannot
 * overlap its own cells, so at worst the deck shrinks.
 */
import type { ReactNode } from 'react'
import { cardSrc, type DeckName } from '../cards/assets'

export type RingPosition = 'n' | 'w' | 'e' | 's'

export interface RingSeat {
  readonly key: string | number
  readonly position: RingPosition
  readonly node: ReactNode
}

export function SeatRing({ seats, centre, className }: { seats: readonly RingSeat[]; centre: ReactNode; className?: string }): JSX.Element {
  return (
    <div className={`seat-ring${className ? ` ${className}` : ''}`}>
      {seats.map((s) => (
        <div key={s.key} className={`ring-seat ring-${s.position}`}>
          {s.node}
        </div>
      ))}
      <div className="ring-centre">{centre}</div>
    </div>
  )
}

/**
 * An empty seat, drawn as the place a card would go: a dashed outline the
 * size of a card with the action written under it. The whole thing is one
 * button, so the target is the size of the place, not of the word.
 */
export function Placemat({
  label,
  onClick,
  lit,
  disabled,
}: {
  label: string
  onClick?: () => void
  /** The seat being offered to this viewer: outlined in brass. */
  lit?: boolean
  disabled?: boolean
}): JSX.Element {
  const body = (
    <>
      <span className="placemat-card" aria-hidden="true" />
      <span className="placemat-label">{label}</span>
    </>
  )
  if (!onClick) {
    return <div className={`placemat${lit ? ' lit' : ''}`}>{body}</div>
  }
  return (
    <button type="button" className={`placemat${lit ? ' lit' : ''}`} onClick={onClick} disabled={disabled}>
      {body}
    </button>
  )
}

/** The deck, face down, with the table's code under it. */
export function TableDeck({
  code,
  deck,
  onShare,
  caption = 'table code',
}: {
  code: string
  deck: DeckName
  onShare?: () => void
  caption?: string
}): JSX.Element {
  const back = cardSrc('BACK', deck)
  const codeNode = (
    <>
      <span className="deck-code index">{code}</span>
      <span className="deck-caption">{caption}</span>
    </>
  )
  return (
    <div className="table-deck">
      <div className="deck-stack" aria-hidden="true">
        <img src={back} alt="" draggable={false} />
        <img src={back} alt="" draggable={false} />
        <img src={back} alt="" draggable={false} />
      </div>
      {onShare ? (
        <button type="button" className="deck-code-btn" onClick={onShare} aria-label={`Share table code ${code.split('').join(' ')}`}>
          {codeNode}
        </button>
      ) : (
        <div className="deck-code-btn">{codeNode}</div>
      )}
    </div>
  )
}
